import { onRequest, RequestMethod } from "common/axios/axios"
import { getApiUrl } from "common/axios/getApiUrl"

/* GET 은 query, POST 는 params.
   onRequest 가 params 를 axios data(body) 에 싣기 때문입니다. GET 에
   params 를 쓰면 서버 @RequestParam 에 아무것도 닿지 않고, onRequest 는
   에러를 삼켜서 조용히 빈 결과가 됩니다 — 화면에서는 "데이터가 없는 것"
   과 구분되지 않습니다. */
export class PlaceReviewRepository {
    // 리뷰 목록 조회
    static getReviews = (placeId: string, page: number = 1, size: number = 10) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/${encodeURIComponent(placeId)}/reviews`,
            method: RequestMethod.GET,
            query: { page, size },
        })
    }

    // 장소 통계 (평균 평점, 리뷰 수)
    static getStats = (placeId: string) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/${encodeURIComponent(placeId)}/stats`,
            method: RequestMethod.GET,
        })
    }

    // 리뷰 작성
    static createReview = (placeId: string, params: {
        placeName?: string;
        meetingId?: string;
        /** 별점은 선택입니다 — 기록은 별점 없이도 남깁니다 */
        rating?: number;
        content: string;
        visitDate?: string;
        tags?: string;
        /** 여행 N일차 (일반 모임은 넘기지 않습니다) */
        dayNumber?: number;
        /** 어느 일정에서 남긴 기록인지 (meeting_sub.sub_id) */
        subId?: number;
    }) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/${encodeURIComponent(placeId)}/reviews`,
            method: RequestMethod.POST,
            params,
        })
    }

    // 리뷰 수정
    static updateReview = (reviewId: number, params: {
        placeId: string;
        rating: number;
        content: string;
        tags?: string;
    }) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/reviews/${reviewId}`,
            method: RequestMethod.PUT,
            params,
        })
    }

    // 리뷰 삭제
    static deleteReview = (reviewId: number) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/reviews/${reviewId}`,
            method: RequestMethod.DELETE,
        })
    }

    /**
     * 기록 신고.
     *
     * 신고는 서버가 판정합니다 — 같은 기록에 신고가 쌓이면 모두에게서 내려갑니다.
     * 같은 사람이 다시 신고해도 한 건으로 셉니다(서버가 멱등 처리).
     */
    static reportReview = (reviewId: number, reasonCode: string, reasonText?: string) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/reviews/${reviewId}/report`,
            method: RequestMethod.POST,
            params: { reasonCode, reasonText: reasonText || null },
        })
    }

    // 리뷰 좋아요 토글
    static toggleLike = (reviewId: number) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/reviews/${reviewId}/like`,
            method: RequestMethod.POST,
        })
    }

    // 사진 목록 조회 (kind: 'STORE' | 'VISIT' — 안 주면 전부)
    static getPhotos = (placeId: string, limit: number = 20, kind?: 'STORE' | 'VISIT') => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/${encodeURIComponent(placeId)}/photos`,
            method: RequestMethod.GET,
            query: kind ? { limit, kind } : { limit },
        })
    }

    // 가게 사진 네 자리 (외관·내부·대표음식·메뉴판, 자리마다 최근 한 장)
    static getStorePhotos = (placeId: string) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/${encodeURIComponent(placeId)}/store-photos`,
            method: RequestMethod.GET,
        })
    }

    // 파일 업로드 (wiz-common) → 업로드된 URL로 사진 등록
    static uploadFile = (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return onRequest({
            url: getApiUrl('fileUpload'),
            method: RequestMethod.POST,
            params: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
        })
    }

    // 사진 업로드 + 등록 (wiz-common 업로드 → dokocrew-api 메타데이터 저장)
    static uploadAndAddPhoto = async (placeId: string, file: File, options?: {
        placeName?: string;
        reviewId?: number;
        meetingId?: string;
        caption?: string;
        photoKind?: 'STORE' | 'VISIT';
        storeSlot?: 'EXTERIOR' | 'INTERIOR' | 'SIGNATURE' | 'MENU';
    }) => {
        // 1) wiz-common에 파일 업로드
        const uploadRes = await PlaceReviewRepository.uploadFile(file);
        const uploadData = uploadRes?.data?.data || uploadRes?.data;
        const imageUrl = uploadData?.fileUrl || uploadData?.url || uploadData?.filePath || '';

        if (!imageUrl) throw new Error('파일 업로드 실패');

        // 2) dokocrew-api에 사진 메타데이터 저장
        return onRequest({
            url: `${getApiUrl('placeReviews')}/${encodeURIComponent(placeId)}/photos`,
            method: RequestMethod.POST,
            params: {
                placeName: options?.placeName,
                reviewId: options?.reviewId,
                meetingId: options?.meetingId,
                imageUrl,
                caption: options?.caption,
                photoKind: options?.photoKind,
                storeSlot: options?.storeSlot,
            },
        })
    }

    // 사진 등록 (이미 업로드된 URL로)
    static addPhoto = (placeId: string, params: {
        placeName?: string;
        reviewId?: number;
        meetingId?: string;
        imageUrl: string;
        thumbnailUrl?: string;
        caption?: string;
        photoKind?: 'STORE' | 'VISIT';
        storeSlot?: 'EXTERIOR' | 'INTERIOR' | 'SIGNATURE' | 'MENU';
    }) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/${encodeURIComponent(placeId)}/photos`,
            method: RequestMethod.POST,
            params,
        })
    }

    // 사진 삭제
    static deletePhoto = (photoId: number) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/photos/${photoId}`,
            method: RequestMethod.DELETE,
        })
    }

    // ══════════════════════════════════════════════════════════════
    // 모임 기록 (여행 일기)
    //
    // 쓰기는 위 createReview / uploadAndAddPhoto 를 그대로 씁니다 —
    // 기록은 장소에 붙는 것이고, 아래는 모임 단위로 모아 보는 길입니다.
    // ══════════════════════════════════════════════════════════════

    /** 모임 기록 목록. sort 가 'day' 면 일차별, 아니면 최신순 */
    static getMeetingJournal = (meetingId: string, query?: {
        sort?: 'day' | 'time';
        page?: number;
        size?: number;
    }) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/journal`,
            method: RequestMethod.GET,
            query,
        })
    }

    /** 메인탭 오늘 카드용 요약 — 사진 몇 장 + 개수 + 최근 한 건 */
    static getMeetingJournalSummary = (meetingId: string, photoLimit = 4) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/journal/summary`,
            method: RequestMethod.GET,
            query: { photoLimit },
        })
    }

    /** 기록 댓글 */
    static getReviewComments = (reviewId: number) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/reviews/${reviewId}/comments`,
            method: RequestMethod.GET,
        })
    }

    static addReviewComment = (reviewId: number, content: string) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/reviews/${reviewId}/comments`,
            method: RequestMethod.POST,
            params: { content },
        })
    }

    static deleteReviewComment = (commentId: number) => {
        return onRequest({
            url: `${getApiUrl('placeReviews')}/reviews/comments/${commentId}`,
            method: RequestMethod.DELETE,
        })
    }

    /** 지금 어디 — 제때 도착하는지. DB 를 쓰지 않는 실시간 조회입니다 */
    static getTraceLive = (meetingId: string, query: {
        /** 안 주면 서버가 모임 일정에서 다음 목적지를 찾습니다 */
        destLat?: number;
        destLng?: number;
        destName?: string;
        /** ISO 8601 */
        scheduledAt?: string;
        /** walking · bicycle · car */
        mode?: string;
    }) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/trace/live`,
            method: RequestMethod.GET,
            query,
        })
    }

    /**
     * 일정 다듬기 — 지난 여행의 발자취에서 찾은 숫자.
     *
     * 숫자는 전부 서버 집계입니다(wiz-ai 호출 없음). 표본이 0이면
     * sampleMeetingCount 가 0 으로 오고 화면은 섹션째 숨깁니다.
     */
    static getTraceInsights = (meetingId: string) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/trace/insights`,
            method: RequestMethod.GET,
        })
    }

    /**
     * 모임 발자취 — 여행이 끝난 뒤 어디를 어떻게 다녔는지.
     * 기간은 서버가 모임 일정에서 만들어 붙입니다(파티션 프루닝).
     */
    static getMeetingTrace = (meetingId: string, day?: number) => {
        return onRequest({
            url: `${getApiUrl('meetingDetail')}/${meetingId}/trace`,
            method: RequestMethod.GET,
            query: day != null ? { day } : undefined,
        })
    }
}
